import { useEffect, useRef, useState } from 'react';
import { Box, withStyles } from 'vopay';
import { Title } from 'UI';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import useCreatePartner from '@/hooks/useApi/useCreatePartner';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import PartnerCreationForm from './PartnerCreationForm';
import { SerializedPartnerCreationValues } from './usePartnerCreationFormConfig';
import styles from './PartnerCreationForm.module.scss';

const cn = withStyles(styles);

export default function PartnerCreationPage() {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Create' });
  const { creating, createPartner } = useCreatePartner();
  const alertDialog = useAlertDialog();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [reviewing, setReviewing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reviewing) containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [reviewing]);

  async function handleSubmit(values: SerializedPartnerCreationValues): Promise<void> {
    try {
      setDataFetchingInfo(true, '', 'fullScreen');
      await createPartner({
        Name: values.PartnerName,
        EmailAddress: values.EmailAddress,
        Country: values.Country,
        PartnerAccountID: values.PartnerAccountID || undefined,
      });
      setDataFetchingInfo(false);

      await alertDialog.success(
        t('successTitle', { defaultValue: 'Partner Created' }),
        t('successMessage', {
          defaultValue: 'The partner has been created and entered the underwriting process.',
        }),
        { confirmLabel: t('successConfirm', { defaultValue: 'Back to Partners' }) },
      );

      history.push('/partners');
    } catch (error) {
      setDataFetchingInfo(false);
      // Return to edit mode so the user can correct the input and retry.
      setReviewing(false);
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Unable to Create Partner' }),
        error instanceof Error && error.message
          ? error.message
          : t('errorMessage', { defaultValue: 'Something went wrong. Please try again.' }),
      );
    }
  }

  return (
    <div className={cn('pageContainer')} ref={containerRef}>
      <Box flx="col" gap="md">
        <Title level="1">{t('pageTitle', { defaultValue: 'Add Partner Account' })}</Title>
        <PartnerCreationForm
          onSubmit={handleSubmit}
          creating={creating}
          reviewing={reviewing}
          onReviewChange={setReviewing}
        />
      </Box>
    </div>
  );
}
